import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButton } from '@components/close-button';
import { Input } from '@components/input';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { ModalV2 } from '@components/modals/modal-v2';
import { resolveAddonPresetName } from '@utils/resolve-addon-preset-name/resolve-addon-preset-name';

import { AddOnsPresetsModalContentProps } from '../add-ons-presets-modal.type';

import { AddOnPresetRow } from './add-on-preset-row';
import { PresetsSkeleton } from './presets-skeleton';

export const ADD_ONS_PRESETS_MODAL_TEST_ID = 'add-ons-presets-modal';

export const AddOnsPresetsModalContent = ({
  isOpen,
  onClose,
  presets,
  isLoading = false,
  selectedIds,
  onToggle,
  onSave,
  isSaving = false,
  surfaceClassName,
  zIndex,
}: AddOnsPresetsModalContentProps) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');

  // This component stays mounted across close/reopen (only ModalV2's children unmount),
  // so clear the query on open to match the container resetting the selection.
  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const isEmpty = !isLoading && presets.length === 0;
  const hasNoSelection = selectedIds.length === 0;

  // Resolve each name once via the shared util (so the filter matches the row label), reused below.
  const namedPresets = presets.map((addon) => ({ addon, name: resolveAddonPresetName(addon, t, i18n.language) }));
  // Plain client-side filter on the already-fetched list (no refetch / debounce needed).
  const query = search.trim().toLowerCase();
  const filteredPresets = query ? namedPresets.filter(({ name }) => name.toLowerCase().includes(query)) : namedPresets;
  // Read only in renderBody, after the isLoading/isEmpty branches have returned early.
  const hasNoResults = filteredPresets.length === 0;

  const renderBody = () => {
    if (isLoading) return <PresetsSkeleton dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--skeleton`} />;

    if (isEmpty) {
      return (
        <div
          className='flex max-sm:min-h-50 items-center justify-center'
          data-testid={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--empty`}>
          <p className='text-secondary text-body-m text-center'>{t('errors:assignAddonsEmptyError')}</p>
        </div>
      );
    }

    return (
      <>
        <Input
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('offers:admissionTicketAddonsSearchPlaceholder')}
          aria-label={t('offers:admissionTicketAddonsSearchPlaceholder')}
          data-testid={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--search`}
        />
        {hasNoResults ? (
          <div
            className='flex min-h-30 items-center justify-center'
            data-testid={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--no-results`}>
            <p className='text-secondary text-body-m text-center'>{t('offers:admissionTicketAddonsNoResultsFound')}</p>
          </div>
        ) : (
          <div className='scrollbar-xceed flex max-h-[420px] flex-col gap-2 overflow-y-auto'>
            {filteredPresets.map(({ addon, name }) => (
              <AddOnPresetRow
                key={addon.id}
                addon={addon}
                name={name}
                checked={selectedIds.includes(addon.id)}
                onToggle={onToggle}
                disabled={isSaving}
                dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--row-${addon.id}`}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={onClose}
      maxWidthClass='sm:max-w-2xl'
      hasMobileHandle
      enableDragToClose
      ariaLabel='add-ons-presets'
      surfaceClassName={surfaceClassName}
      zIndex={zIndex}>
      <ModalStickyHeader
        title={t('offers:admissionAddOnsSectionTitle')}
        customRightElement={
          <div className='hidden sm:block'>
            <CloseButton
              handleClose={onClose}
              id={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--close`}
              dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--close`}
            />
          </div>
        }
      />

      <div className='flex flex-col gap-4 px-6 py-4'>{renderBody()}</div>

      <ModalStickyFooter
        onSecondaryButtonClick={onClose}
        secondaryButtonText={t('common:cancel')}
        secondaryDataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--cancel`}
        onPrimaryButtonClick={onSave}
        primaryButtonText={t('common:save')}
        primaryDataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--save`}
        isSubmitDisabled={isEmpty || hasNoSelection}
        isSubmitLoading={isSaving}
      />
    </ModalV2>
  );
};
